/**
 * @typedef {Object} PricingConfig
 * @property {number} monthlyPrice - Monthly subscription price (HT)
 * @property {number} yearlyPrice - Yearly subscription price (HT)
 * @property {boolean} hasYearlyDiscount - Whether yearly plan has a discount
 */

import { browser } from "$app/environment";

/**
 * @typedef {Object} PromoConfig
 * @property {PricingConfig} pricing - Pricing configuration
 * @property {string} id - Promotion code
 * @property {string} stripeCode - Stripe promotion code
 * @property {string} title - Promotion title
 * @property {string} logo - Path to promotion logo image
 * @property {string} description - Promotion description
 * @property {number} discount - Discount percentage
 * @property {string} discountLabel - Discount description
 */

// TVA standard à 20%
const VAT_RATE = 0.2;

/**
 * Calculate TTC price from HT price
 * @param {number} priceHT - Price without VAT
 * @returns {number} - Price with VAT, rounded to nearest integer
 */
export const calculateTTC = (priceHT) => Math.round(priceHT * (1 + VAT_RATE));

/** @type {PricingConfig} */
export const standardPricing = {
    monthlyPrice: 57.5,
    yearlyPrice: 46,
    hasYearlyDiscount: true
};

/** @type {PromoConfig[]} */
export const promoConfigs = [
    {
        pricing: {
            monthlyPrice: 41,
            yearlyPrice: 41,
            hasYearlyDiscount: false
        },
        id: 'BARREAU_PARIS',
        stripeCode: 'promo_1Qfj5uEZeSfMgOFQmcX3wxlx',
        logo: "https://www.avocatparis.org/themes/custom/bdp/logo.svg",
        title: "Ordalie x Barreau de Paris",
        description: "Offre exclusive réservée aux avocats du Barreau de Paris",
        discount: 25,
        discountLabel: "Réduction partenaire appliquée"
    }
];

/**
 * Gets promotion configuration from URL or localStorage
 * @returns {PromoConfig|null} Promotion configuration or null if not found
 */
export const getPromoFromUrl = () => {
    if (typeof window === 'undefined') return null;
    const urlParams = new URLSearchParams(window.location.search);
    const promoCode = urlParams.get('promo');

    // If URL has a valid promo code, save it
    const promo = promoConfigs.find(p => p.id === promoCode);
    if (promoCode && promo) {
        localStorage.setItem('ordalie_promo', promoCode);
        return promo;
    }

    // If URL has no promo code, clear any stored promo
    if (!promoCode) {
        localStorage.removeItem('ordalie_promo');
        return null;
    }

    // If we have a stored promo but invalid URL promo, keep using stored promo
    const storedPromo = localStorage.getItem('ordalie_promo');
    return storedPromo ? promoConfigs.find(p => p.id === storedPromo) || null : null;
};

/**
 * Gets promotion configuration from localStorage
 * @returns {PromoConfig|null} Promotion configuration or null if not found
 */
export const getPromoCode = () => {
    if (typeof window === 'undefined') return null;
    const storedPromo = localStorage?.getItem('ordalie_promo');
    return storedPromo ? promoConfigs.find(p => p.id === storedPromo) || null : null;
};
